import { Link } from '@tanstack/react-router'
import { Database } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useDocumentedDatabases } from '@/features/database/docs/queries'
import { useSessionStore } from '@/features/auth/session-store'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'

/**
 * Which database to see the reference for.
 *
 * Outside the shell, because the reference itself is: it is a document to be
 * read alongside an editor rather than a screen of the application, and a
 * sidebar full of tables next to it is the wrong thing to be looking at.
 */
export function ApiDocsPage() {
  const { t } = useTranslation()
  const authenticated = useSessionStore((state) => state.authenticated)
  const { databases, isPending } = useDocumentedDatabases()

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 p-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold">{t('apiDocs.title')}</h1>
        <p className="text-muted-foreground text-sm">{t('apiDocs.intro')}</p>
      </header>

      {!authenticated ? (
        // The endpoints are documented per database, and which databases exist
        // is a question about the reader. There is nothing to show until there
        // is somebody to show it to.
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('apiDocs.signInFirst')}</EmptyTitle>
          </EmptyHeader>
          <Button asChild>
            <Link to="/login" search={{ original: '/api-docs' }}>
              {t('action.signIn')}
            </Link>
          </Button>
        </Empty>
      ) : isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : databases.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('apiDocs.noDatabases')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      ) : (
        <section className="space-y-2">
          <h2 className="text-sm font-medium">{t('apiDocs.chooseDatabase')}</h2>
          <ul className="divide-y rounded-lg border">
            {databases.map(({ workspace, database }) => (
              <li key={database.id}>
                <Link
                  to="/api-docs/database/$databaseId"
                  params={{ databaseId: database.id }}
                  className="hover:bg-accent/40 flex items-center gap-3 p-3"
                >
                  <Database className="text-muted-foreground size-4 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {database.name}
                    </span>
                    <span className="text-muted-foreground block truncate text-xs">
                      {workspace.name}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Button asChild variant="ghost" size="sm">
        <Link to="/dashboard">{t('apiDocs.backToDashboard')}</Link>
      </Button>
    </div>
  )
}
